import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Title,
  Container,
  Textarea,
  Button,
  Text,
  TextInput,
  Paper,
  Checkbox,
  Grid,
  Group,
  Stack,
  Collapse,
  Box,
  List,
  Loader,
  Flex,
  UnstyledButton,
} from "@mantine/core";
import {
  IconWriting,
  IconCpu,
  IconChecklist,
  IconRefresh,
  IconDownload,
} from "@tabler/icons-react";
import { useAuthCheck } from "../hooks/useAuthCheck";
import { useNotification } from "../hooks/useNotification";
import { documentRequest, generateDocuments, getJobStatus } from "../api";
import { NOTIFICATION_MESSAGES } from "../constants/notificationMessages";
import classes from "./Socius.module.css";

function SafeGenerator() {
  const { isAuthenticated, isLoading } = useAuthCheck();
  const navigate = useNavigate();
  const [safeTerms, setSafeTerms] = useState("");
  const [fieldInputs, setFieldInputs] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [safeCreated, setSafeCreated] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [inputError, setInputError] = useState(false);
  const [documentUrls, setDocumentUrls] = useState(null);
  const [mfnState, setMfnState] = useState(false);
  const [formattedInputs, setFormattedInputs] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const [filledFields, setFilledFields] = useState([]);
  const [previewKey, setPreviewKey] = useState(0);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [isGeneratingDocuments, setIsGeneratingDocuments] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);

  const { showNotification, updateNotification } = useNotification();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (lastUpdateTime) {
      const updateTimes = [10000, 20000, 30000];
      let currentUpdateIndex = 0;

      const scheduleNextUpdate = () => {
        const now = new Date();
        const timeSinceLastUpdate = now - lastUpdateTime;

        if (timeSinceLastUpdate < 60000) {
          // If less than a minute has passed, use the current update interval
          const currentInterval = updateTimes[currentUpdateIndex];
          setTimeout(() => {
            setLastUpdateTime(new Date(lastUpdateTime.getTime()));
            currentUpdateIndex = Math.min(
              currentUpdateIndex + 1,
              updateTimes.length - 1,
            );
            scheduleNextUpdate();
          }, currentInterval);
        } else {
          // If more than a minute has passed, switch to minute updates
          const timer = setInterval(() => {
            setLastUpdateTime((prevTime) => new Date(prevTime.getTime()));
          }, 60000);
          return () => clearInterval(timer);
        }
      };

      return scheduleNextUpdate();
    }
  }, [lastUpdateTime]);

  if (isLoading) {
    return (
      <Container size="xl">
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Loader type="dots" size="xl" />
        </Box>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return null; // This will prevent rendering anything while redirecting
  }

  const resetState = () => {
    setSafeTerms("");
    setFieldInputs(null);
    setFileId(null);
    setLoading(false);
    setSafeCreated(false);
    setCurrentStep(0);
    setInputError(false);
    setDocumentUrls(null);
    setMfnState(false);
    setFormattedInputs({});
    setPreviewUrl(null);
    setFilledFields([]);
  };

  const formatCurrency = (value) => {
    const numericValue = parseFloat(value.replace(/[^0-9.]/g, ""));
    if (isNaN(numericValue)) return "";
    return numericValue % 1 === 0
      ? numericValue.toLocaleString("en-US")
      : numericValue.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
  };

  const formatPercentage = (value) => {
    const numericValue = parseFloat(value.replace(/[^0-9.]/g, ""));
    return isNaN(numericValue) ? "" : `${numericValue.toFixed(2)}%`;
  };

  const pollJobStatus = async (jobId, onSuccess, onError) => {
    try {
      const response = await getJobStatus(jobId);
      if (response.status === "completed") {
        onSuccess(response.result);
      } else if (response.status === "error") {
        onError(response.error);
      } else {
        setTimeout(() => pollJobStatus(jobId, onSuccess, onError), 2000);
      }
    } catch (error) {
      onError(error.message);
    }
  };

  const handlePrepareSafeForm = async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setInputError(false);

    const notificationId = showNotification(
      NOTIFICATION_MESSAGES.PREPARE_SAFE.LOADING.title,
      NOTIFICATION_MESSAGES.PREPARE_SAFE.LOADING.message,
    );

    try {
      const result = await documentRequest(safeTerms);
      pollJobStatus(
        result.trace_id,
        async (prepareResult) => {
          // handle success for prepare safe form
          if (prepareResult.template_name === null) {
            updateNotification(
              notificationId,
              NOTIFICATION_MESSAGES.PREPARE_SAFE.ERROR.title,
              "Couldn't determine which type of SAFE to prepare. Please provide more detail.",
              "error",
            );
            setInputError(true);
            return;
          }

          setFieldInputs(prepareResult);
          setFormattedInputs(
            initializeFormattedInputs(prepareResult.field_dict),
          );
          setMfnState(
            prepareResult.field_dict["INCLUDE MFN"]?.toLowerCase() === "yes",
          );
          setFileId(prepareResult.file_id);

          updateNotification(
            notificationId,
            NOTIFICATION_MESSAGES.PREPARE_SAFE.SUCCESS.title,
            NOTIFICATION_MESSAGES.PREPARE_SAFE.SUCCESS.message,
            "success",
          );

          setCurrentStep(1);

          // Start generating documents asynchronously
          setIsGeneratingDocuments(true);
          try {
            const generateResult = await generateDocuments(
              prepareResult.template_name,
              prepareResult.field_dict,
              prepareResult.file_id,
              "SAFE",
            );

            pollJobStatus(
              generateResult.trace_id,
              (documentResult) => {
                if (documentResult && typeof documentResult === "object") {
                  setDocumentUrls({
                    output_docx_url: documentResult.output_docx_url || "",
                    output_pdf_url: documentResult.output_pdf_url || "",
                    redline_url: documentResult.redline_url || "",
                    // redline_pdf_url: documentResult.redline_pdf_url || ''
                  });
                  setPreviewUrl(documentResult.redline_url || "");
                  setPreviewKey((prevKey) => prevKey + 1);
                  setLastUpdateTime(new Date());
                } else {
                  throw new Error("Invalid document generation result");
                }
                setIsGeneratingDocuments(false);
              },
              (error) => {
                console.error("Error generating documents:", error);
                updateNotification(
                  showNotification(
                    NOTIFICATION_MESSAGES.CREATE_SAFE.ERROR.title,
                    error.message ||
                      NOTIFICATION_MESSAGES.CREATE_SAFE.ERROR.message,
                    "error",
                  ),
                );
                setIsGeneratingDocuments(false);
              },
            );
          } catch (error) {
            console.error("Error starting document generation:", error);
            updateNotification(
              showNotification(
                NOTIFICATION_MESSAGES.CREATE_SAFE.ERROR.title,
                error.message ||
                  NOTIFICATION_MESSAGES.CREATE_SAFE.ERROR.message,
                "error",
              ),
            );
            setIsGeneratingDocuments(false);
          }
        },
        (error) => {
          // handle error for prepare safe form
          updateNotification(
            notificationId,
            NOTIFICATION_MESSAGES.PREPARE_SAFE.ERROR.title,
            error.response?.data?.detail ||
              NOTIFICATION_MESSAGES.PREPARE_SAFE.ERROR.message,
            "error",
          );
          setInputError(true);
        },
      );
    } catch (error) {
      console.error("Error preparing SAFE form:", error);
      updateNotification(
        notificationId,
        NOTIFICATION_MESSAGES.PREPARE_SAFE.ERROR.title,
        error.response?.data?.detail ||
          NOTIFICATION_MESSAGES.PREPARE_SAFE.ERROR.message,
        "error",
      );
      setInputError(true);
    } finally {
      setLoading(false);
    }
  };

  const initializeFormattedInputs = (fieldDict) => {
    const initialFormattedInputs = {};
    Object.entries(fieldDict).forEach(([key, value]) => {
      if (value === `[${key}]`) {
        initialFormattedInputs[key] = "";
      } else if (["VALUATION_CAP", "PURCHASE_AMOUNT"].includes(key)) {
        // Updated field names
        initialFormattedInputs[key] = formatCurrency(value);
      } else if (key === "DISCOUNT_RATE") {
        // Updated field name
        initialFormattedInputs[key] = formatPercentage(value);
      } else {
        initialFormattedInputs[key] = value;
      }
    });
    return initialFormattedInputs;
  };

  const handleInputChange = (key, value) => {
    let formattedValue = value;
    setFormattedInputs((prev) => ({ ...prev, [key]: formattedValue }));
  };

  const handleCreateSafe = async () => {
    if (!isAuthenticated) return;

    setLoading(true);

    const notificationId = showNotification(
      NOTIFICATION_MESSAGES.CREATE_SAFE.LOADING.title,
      NOTIFICATION_MESSAGES.CREATE_SAFE.LOADING.message,
    );

    try {
      const safeData = {};

      Object.entries(formattedInputs).forEach(([key, value]) => {
        if (value === "" && fieldInputs.field_dict[key] === `[${key}]`) {
          safeData[key] = `[${key}]`;
        } else {
          // Preserve the user's input exactly as entered
          safeData[key] = value;
        }
      });

      // Convert the MFN checkbox to 'Yes' or 'No'
      safeData["include_mfn"] = mfnState ? "yes" : "no";

      const result = await generateDocuments(
        fieldInputs.template_name,
        safeData,
        fileId,
        "SAFE",
      );
      pollJobStatus(
        result.trace_id,
        (result) => {
          // handle success
          setDocumentUrls(result);
          setPreviewUrl(result.redline_url);
          setPreviewKey((prevKey) => prevKey + 1);
          setIframeLoading(true);
          setLastUpdateTime(new Date());

          updateNotification(
            notificationId,
            NOTIFICATION_MESSAGES.CREATE_SAFE.SUCCESS.title,
            NOTIFICATION_MESSAGES.CREATE_SAFE.SUCCESS.message,
            "success",
          );

          setSafeCreated(true);
          setCurrentStep(2);
        },
        (error) => {
          // handle error
          console.error("Error creating SAFE:", error);
          updateNotification(
            notificationId,
            NOTIFICATION_MESSAGES.CREATE_SAFE.ERROR.title,
            error.response?.data?.detail ||
              NOTIFICATION_MESSAGES.CREATE_SAFE.ERROR.message,
            "error",
          );
        },
      );
    } catch (error) {
      console.error("Error creating SAFE:", error);
      updateNotification(
        notificationId,
        NOTIFICATION_MESSAGES.CREATE_SAFE.ERROR.title,
        error.response?.data?.detail ||
          NOTIFICATION_MESSAGES.CREATE_SAFE.ERROR.message,
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (url) => {
    window.open(url, "_blank");
  };

  // Helper function to convert to Title Case
  const toTitleCase = (str) => {
    const smallWords =
      /^(a|an|and|as|at|but|by|en|for|if|in|of|on|or|the|to|vs?\.?|via)$/i;
    return str.replace(/\w\S*/g, (txt, index) => {
      if (index !== 0 && smallWords.test(txt)) {
        return txt.toLowerCase();
      }
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  };

  const formatLastUpdateTime = () => {
    if (!lastUpdateTime) return "";
    const now = new Date();
    const diff = Math.floor((now - lastUpdateTime) / 1000);
    if (diff < 10) return "Just now";
    if (diff < 30) return "10 secs ago";
    if (diff < 60) return "30 secs ago";
    if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  return (
    <Container size="xl">
      {/* Title Container */}
      <Container size="sm" p={0} mb="lg">
        <Group position="apart" mb="md">
          <Title order={3}>SAFE Generator</Title>
          {currentStep > 0 && (
            <Button
              leftSection={<IconRefresh size={18} />}
              variant="outline"
              onClick={resetState}
              size="sm"
            >
              Start Over
            </Button>
          )}
        </Group>
      </Container>

      {/* Step 1 Container*/}
      <Container size="sm" p={0} mb="lg">
        <Paper shadow="xs" p="md" withBorder>
          <Stack spacing="md">
            {/* Instructions */}
            <Collapse
              in={currentStep === 0}
              transitionDuration={400}
              transitionTimingFunction="ease"
            >
              <Box>
                <List size="md" withPadding>
                  <List.Item icon={<IconWriting size={24} />} mb="sm">
                    Enter a natural language description of the SAFE and click{" "}
                    <b>"Prepare Template"</b> to prompt AI.
                  </List.Item>
                  <List.Item icon={<IconCpu size={24} />} mb="sm">
                    AI will identify appropriate SAFE inputs and provide a list
                    for your review.
                  </List.Item>
                  <List.Item icon={<IconChecklist size={24} />} mb="sm">
                    After finalizing, select <b>"Generate SAFE"</b> to create
                    your documents (.docx, .pdf, and redline).
                  </List.Item>
                </List>
              </Box>
            </Collapse>

            {/* Input */}
            <Textarea
              value={safeTerms}
              onChange={(e) => {
                setSafeTerms(e.target.value);
                setInputError(false);
              }}
              placeholder="Ex. I need a 50k SAFE for XYZ, Inc. with a 20m val cap and an MFN. John Doe will be signing for the company."
              autosize
              disabled={currentStep >= 1}
              readOnly={currentStep >= 1}
              error={inputError}
            />
            {currentStep === 0 && (
              <Button
                onClick={handlePrepareSafeForm}
                loading={loading}
                loaderProps={{ type: "dots" }}
              >
                Prepare Template
              </Button>
            )}
          </Stack>
        </Paper>
      </Container>

      {/* Steps 2 and 3 Container */}
      {currentStep >= 1 && (
        <Grid gutter="md">
          {/* Form Column */}
          <Grid.Col span={6}>
            <Paper shadow="xs" p="md" withBorder>
              <Stack spacing="md">
                <Text size="sm" className={classes.titleText}>
                  Review and finalize the prepared fields below
                </Text>
                <form id="safeForm">
                  <Grid gutter="md">
                    {Object.entries(fieldInputs.field_dict).map(
                      ([key, value]) => (
                        <Grid.Col
                          key={key}
                          span={
                            key === "include_mfn"
                              ? 12
                              : { base: 12, sm: 6, md: 4 }
                          }
                        >
                          {key === "include_mfn" ? (
                            <UnstyledButton
                              onClick={() => setMfnState(!mfnState)}
                              style={{
                                width: "100%",
                                padding: "10px",
                                border: "1px solid #e0e0e0",
                                borderRadius: "4px",
                              }}
                              disabled={safeCreated}
                            >
                              <Group>
                                <Checkbox
                                  checked={mfnState}
                                  onChange={() => {}}
                                  tabIndex={-1}
                                  size="md"
                                  mr="xl"
                                  styles={{ input: { cursor: "pointer" } }}
                                  aria-hidden
                                />
                                <div>
                                  <Text fw={500} mb={7} lh={1}>
                                    Include Most Favored Nation (MFN) Provision
                                  </Text>
                                  <Text fz="sm" c="dimmed">
                                    Check to include MFN provision in your SAFE
                                  </Text>
                                </div>
                              </Group>
                            </UnstyledButton>
                          ) : (
                            <TextInput
                              classNames={classes}
                              label={toTitleCase(key.replace(/_/g, " "))} // Convert underscores to spaces for display
                              value={formattedInputs[key]}
                              onChange={(e) =>
                                handleInputChange(key, e.target.value)
                              }
                              name={key}
                              disabled={safeCreated}
                              readOnly={safeCreated}
                            />
                          )}
                        </Grid.Col>
                      ),
                    )}
                  </Grid>
                </form>
                {!safeCreated && (
                  <Button
                    onClick={handleCreateSafe}
                    loading={loading}
                    loaderProps={{ type: "dots" }}
                  >
                    Generate SAFE
                  </Button>
                )}
              </Stack>
            </Paper>
          </Grid.Col>

          {/* Preview Column */}
          <Grid.Col span={6}>
            <Paper shadow="xs" p="md" withBorder>
              {documentUrls && currentStep >= 1 && (
                <Stack spacing="md" mb="lg">
                  <Group position="apart" align="center">
                    <Text size="sm" className={classes.titleText}>
                      Files available for download:
                    </Text>
                    {lastUpdateTime && (
                      <Text size="xs" c="dimmed">
                        Last updated: {formatLastUpdateTime()}
                      </Text>
                    )}
                  </Group>
                  <Flex
                    gap="xs"
                    mb="sm"
                    align="center"
                    justify="center"
                    wrap="wrap"
                  >
                    <Button
                      style={{ overflow: "visible" }}
                      size="sm"
                      rightSection={<IconDownload size={18} />}
                      onClick={() =>
                        handleDownload(documentUrls.output_docx_url)
                      }
                      loading={loading}
                      loaderProps={{ type: "dots" }}
                    >
                      SAFE (DOCX)
                    </Button>
                    <Button
                      style={{ overflow: "visible" }}
                      size="sm"
                      rightSection={<IconDownload size={18} />}
                      onClick={() =>
                        handleDownload(documentUrls.output_pdf_url)
                      }
                      loading={loading}
                      loaderProps={{ type: "dots" }}
                    >
                      SAFE (PDF)
                    </Button>
                    <Button
                      style={{ overflow: "visible" }}
                      size="sm"
                      rightSection={<IconDownload size={18} />}
                      onClick={() => handleDownload(documentUrls.redline_url)}
                      loading={loading}
                      loaderProps={{ type: "dots" }}
                    >
                      Track Changes (DOCX)
                    </Button>
                    {/* <Button style={{ overflow: 'visible' }} size="sm" rightSection={<IconDownload size={18} />} onClick={() => handleDownload(documentUrls.redline_pdf_url)} loading={loading} loaderProps={{ type: 'dots' }}>Redline (PDF)</Button> */}
                  </Flex>
                </Stack>
              )}
              {isGeneratingDocuments ? (
                <Box>
                  <Text
                    size="sm"
                    mb="md"
                    className={[classes.loadingText, classes.titleText].join(
                      " ",
                    )}
                  >
                    Generating documents
                  </Text>
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Loader type="dots" />
                  </Box>
                </Box>
              ) : previewUrl ? (
                <Box>
                  <Text size="sm" mb="md" className={classes.titleText}>
                    Preview Redline
                  </Text>
                  {iframeLoading && (
                    <Box
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <Loader type="dots" />
                    </Box>
                  )}
                  <iframe
                    key={previewKey}
                    src={`https://docs.google.com/gview?url=${previewUrl}&embedded=true`}
                    width="100%"
                    height="530px"
                    // style={{ display: iframeLoading ? 'none' : 'block' }}
                    onLoad={() => setIframeLoading(false)}
                  />
                </Box>
              ) : (
                <Box>
                  <Text size="lg" mb="md">
                    Preview will appear here after generating SAFE
                  </Text>
                </Box>
              )}
            </Paper>
          </Grid.Col>
        </Grid>
      )}

      {/* Download Container */}
    </Container>
  );
}

export default SafeGenerator;
